import * as screenfull from 'screenfull';
import {
    all, call, put, select, takeLatest,
} from 'redux-saga/effects';

import {
    closeWorkspaceOverlay,
    featuresDoneSetup,
    loadWorkspacePreset,
    resetActiveOverlay,
    setSplitViewItem,
} from 'common/store/workspaces/actions';
import { WorkspaceTypeKeys } from 'common/store/workspaces/types';
import { WorkspacesState } from 'common/store/workspaces/reducer';

import commonApiService from 'common/services/api/commonApiService';
import { ContextTypeKeys } from '../context/types';
import { ContextState } from '../context/reducer';

const getContextState = (state: { context: any }) => state.context;
const getWorkspaceState = (state: { workspaces: any }) => state.workspaces;

function* callFetchWorkspace() {
    const contextState: ContextState = yield select(getContextState);
    const workspacesState: WorkspacesState = yield select(getWorkspaceState);

    const { workspaceId } = contextState;
    const { workspaceOverlay, activeOverlay } = workspacesState;

    try {
        if (workspaceId) {
            yield put(loadWorkspacePreset(null));
            /*  when workspaceId updated from side bar,
                closeWorkspaceOverlay should not be called all the time unless there is an overlay related to workspaceId,
                like the columnsPicker overlay
            */
            if (workspaceOverlay && workspaceOverlay.workspaceOverlayName.includes(workspaceId)) {
                yield put(closeWorkspaceOverlay());
            }

            if (activeOverlay) {
                yield put(resetActiveOverlay());
            }
            const data = yield call(commonApiService.fetchWorkspace, workspaceId);
            yield put(loadWorkspacePreset(data));
        }
    } catch (error) {
        console.error(error);
        yield put({ error, type: 'FETCH_WORKSPACE_FAILED' });
        yield put(loadWorkspacePreset(null));
    }
}

function* callFullScreenChange() {
    const workspacesState: WorkspacesState = yield select(getWorkspaceState);

    const { fullScreenMode } = workspacesState;

    if (screenfull.enabled) {
        if (fullScreenMode) {
            screenfull.request();
        } else {
            screenfull.exit();
        }
    }
}

function* callFeatureSetupDone(action: any) {
    const workspacesState: WorkspacesState = yield select(getWorkspaceState);
    const contextState: ContextState = yield select(getContextState);
    const featureId: string = action.payload;
    const { featuresCount } = workspacesState;

    /* only trigger featuresDoneSetup action on workspace features(featureId includes workspaceId),
        such as committeeSelector overlay, workflow overlay are not workspace features
     */
    if (featuresCount <= 0 && featureId && featureId.includes(contextState.workspaceId)) {
        yield put(featuresDoneSetup());
    }
}

function* callSplitViewChange() {
    const contextState: ContextState = yield select(getContextState);
    const workspaceState: WorkspacesState = yield select(getWorkspaceState);
    let { splitPreviewItem } = workspaceState;

    if (!splitPreviewItem) {
        splitPreviewItem = contextState.currentQtiContextItem;
    }

    if (contextState.currentQtiContextItem) {
        const {
            objectId, objectType, category, title, orderNumber,
        } = splitPreviewItem;
        yield put(setSplitViewItem({
            objectId, objectType, category, title, orderNumber,
        }));
    } else if (contextState.currentResourceContextItem?.type) {
        const { contentItemId, type } = contextState.currentResourceContextItem;
        yield put(setSplitViewItem({
            objectId: contentItemId,
            objectType: type,
        }));
    }
}

function* createPrintEventMessageSaga(action: any) {
    yield call(commonApiService.createPrintEventMessage, action.payload);
}

function* watchWorkspaceIdChange() {
    yield takeLatest(ContextTypeKeys.WORKSPACE_ID_UPDATED, callFetchWorkspace);
}

function* watchFullScreenChange() {
    yield takeLatest(WorkspaceTypeKeys.TOGGLE_FULL_SCREEN, callFullScreenChange);
}

function* watchFeatureLoaded() {
    yield takeLatest(WorkspaceTypeKeys.FEATURE_LOADED, callFeatureSetupDone);
}

function* watchSplitViewIdChange() {
    yield takeLatest(WorkspaceTypeKeys.OPEN_CURRENT_CONTEXT_IN_SPLIT, callSplitViewChange);
}

function* watchCreatePrintEventMessage() {
    yield takeLatest(WorkspaceTypeKeys.CREATE_PRINT_EVENT_MESSAGE, createPrintEventMessageSaga);
}

function* rootWorkspacesSaga() {
    yield all([
        watchFeatureLoaded(),
        watchWorkspaceIdChange(),
        watchFullScreenChange(),
        watchSplitViewIdChange(),
        watchCreatePrintEventMessage(),
    ]);
}

export default rootWorkspacesSaga;
